// Pure logic for the Everyday Services (commerce) platform page — no React, no
// network, so it can be unit-tested. Mirrors commerce-service's gates exactly
// (internal/commerce/service.go `purchasable` + `route`):
//   - global, country:XX, product:X and provider:x are FAIL-CLOSED: no row = OFF.
//   - operator:X is the opposite: no row = ON; only an explicit OFF row blocks.

export interface CommerceSwitch {
  scope: string;
  enabled: boolean;
  reason?: string;
  updated_at?: string;
}

export type ReconReason = 'provider_outcome_unknown' | 'payment_outcome_unknown' | string;
export type ResolveOutcome = 'delivered' | 'not_delivered' | 'collected' | 'not_collected' | 'refunded';

export interface SwitchDef {
  scope: string;
  label: string;
  hint?: string;
  /** What a missing row means: false for fail-closed scopes, true for networks. */
  missingMeans: boolean;
}

export interface SwitchGroup {
  key: string;
  title: string;
  description: string;
  items: SwitchDef[];
}

export const COUNTRY = 'TZ';

export const PRODUCTS: { code: string; label: string }[] = [
  { code: 'AIRTIME', label: 'Airtime' },
  { code: 'DATA_BUNDLE', label: 'Data bundles' },
  { code: 'ELECTRICITY', label: 'Electricity (LUKU)' },
];

export const NETWORKS: { code: string; label: string }[] = [
  { code: 'VODACOM', label: 'Vodacom' },
  { code: 'AIRTEL', label: 'Airtel' },
  { code: 'TIGO', label: 'Mixx by Yas (Tigo)' },
  { code: 'HALOTEL', label: 'Halotel' },
  { code: 'TTCL', label: 'TTCL' },
];

// Providers we always show (even with no row yet, i.e. OFF). Any other
// provider:<code> row the backend returns is shown too.
export const KNOWN_PROVIDERS: { code: string; label: string; hint?: string }[] = [
  { code: 'selcom', label: 'Selcom' },
  { code: 'mock', label: 'Mock provider', hint: 'Test provider — never enable outside dev' },
];

const PRODUCT_LABEL: Record<string, string> = Object.fromEntries(PRODUCTS.map((p) => [p.code, p.label]));

export const productLabel = (code: string | undefined | null): string =>
  (code && PRODUCT_LABEL[code]) || code || '—';

/** Build the matrix of switches the page renders, merging in any extra provider/other rows. */
export function buildSwitchGroups(rows: CommerceSwitch[]): SwitchGroup[] {
  const providerCodes = new Set(KNOWN_PROVIDERS.map((p) => p.code));
  const extraProviders: SwitchDef[] = [];
  const other: SwitchDef[] = [];
  const known = new Set<string>([
    'global',
    `country:${COUNTRY}`,
    ...PRODUCTS.map((p) => `product:${p.code}`),
    ...NETWORKS.map((n) => `operator:${n.code}`),
    ...KNOWN_PROVIDERS.map((p) => `provider:${p.code}`),
  ]);
  for (const r of rows) {
    if (known.has(r.scope)) continue;
    if (r.scope.startsWith('provider:')) {
      const code = r.scope.slice('provider:'.length);
      if (!providerCodes.has(code)) {
        providerCodes.add(code);
        extraProviders.push({ scope: r.scope, label: code, missingMeans: false });
      }
    } else if (r.scope.startsWith('operator:')) {
      other.push({ scope: r.scope, label: r.scope, missingMeans: true });
    } else {
      other.push({ scope: r.scope, label: r.scope, missingMeans: false });
    }
  }

  const groups: SwitchGroup[] = [
    {
      key: 'global',
      title: 'Global',
      description: 'The master switch. Off = no purchases anywhere.',
      items: [{ scope: 'global', label: 'Everyday Services (all purchases)', missingMeans: false }],
    },
    {
      key: 'country',
      title: 'Country',
      description: 'Off unless explicitly switched on.',
      items: [{ scope: `country:${COUNTRY}`, label: 'Tanzania (TZ)', missingMeans: false }],
    },
    {
      key: 'products',
      title: 'Products',
      description: 'Each product is off unless explicitly switched on.',
      items: PRODUCTS.map((p) => ({ scope: `product:${p.code}`, label: p.label, missingMeans: false })),
    },
    {
      key: 'networks',
      title: 'Mobile networks',
      description: 'Networks are ON unless explicitly switched off.',
      items: NETWORKS.map((n) => ({ scope: `operator:${n.code}`, label: n.label, missingMeans: true })),
    },
    {
      key: 'providers',
      title: 'Providers',
      description: 'A provider is off unless explicitly switched on. Orders are only routed to providers that are on.',
      items: [
        ...KNOWN_PROVIDERS.map((p) => ({ scope: `provider:${p.code}`, label: p.label, hint: p.hint, missingMeans: false })),
        ...extraProviders,
      ],
    },
  ];
  if (other.length) {
    groups.push({ key: 'other', title: 'Other switches', description: 'Further switches recorded in the service.', items: other });
  }
  return groups;
}

/** The effective state of a scope: the row if present, else the scope's default. */
export function effectiveOn(rows: CommerceSwitch[], scope: string, missingMeans: boolean): boolean {
  const r = rows.find((x) => x.scope === scope);
  return r ? r.enabled : missingMeans;
}

export interface PurchaseSummary {
  possible: boolean;
  /** Why purchases are off (when possible=false). */
  blockers: string[];
  /** Partial restrictions while purchases are still possible. */
  warnings: string[];
}

/**
 * Are purchases possible at all? Mirrors the backend: global AND country AND at
 * least one product AND at least one provider must be explicitly ON.
 * Only meaningful when the switches actually LOADED — callers must render
 * loading/error states separately and never call this on a failed fetch.
 */
export function purchaseSummary(rows: CommerceSwitch[]): PurchaseSummary {
  const blockers: string[] = [];
  const warnings: string[] = [];
  if (!effectiveOn(rows, 'global', false)) blockers.push('the global switch is off');
  if (!effectiveOn(rows, `country:${COUNTRY}`, false)) blockers.push(`country ${COUNTRY} is off`);

  const productsOn = PRODUCTS.filter((p) => effectiveOn(rows, `product:${p.code}`, false));
  if (productsOn.length === 0) blockers.push('every product is off');
  else if (productsOn.length < PRODUCTS.length) {
    const off = PRODUCTS.filter((p) => !productsOn.includes(p)).map((p) => p.label);
    warnings.push(`${off.join(', ')} ${off.length === 1 ? 'is' : 'are'} off`);
  }

  const providerScopes = new Set<string>([
    ...KNOWN_PROVIDERS.map((p) => `provider:${p.code}`),
    ...rows.filter((r) => r.scope.startsWith('provider:')).map((r) => r.scope),
  ]);
  const providersOn = [...providerScopes].filter((s) => effectiveOn(rows, s, false));
  if (providersOn.length === 0) blockers.push('no provider is on');

  const networksOff = NETWORKS.filter((n) => !effectiveOn(rows, `operator:${n.code}`, true));
  const airtimeOrDataOn = productsOn.some((p) => p.code !== 'ELECTRICITY');
  if (networksOff.length === NETWORKS.length && airtimeOrDataOn) {
    warnings.push('every mobile network is off, so airtime and data cannot be bought');
  } else if (networksOff.length > 0) {
    warnings.push(`${networksOff.map((n) => n.label).join(', ')} ${networksOff.length === 1 ? 'is' : 'are'} off`);
  }

  return { possible: blockers.length === 0, blockers, warnings };
}

export function summaryLine(s: PurchaseSummary): string {
  if (!s.possible) return `Purchases are OFF: ${s.blockers.join('; ')}.`;
  if (s.warnings.length) return `Purchases are ON, with limits: ${s.warnings.join('; ')}.`;
  return 'Purchases are ON for every product and network.';
}

// ---------------------------------------------------------------------------
// Reconciliation
// ---------------------------------------------------------------------------

export const REASON_LABEL: Record<string, string> = {
  provider_outcome_unknown: 'Delivery outcome unknown',
  payment_outcome_unknown: 'Payment outcome unknown',
  refund_unresolved: 'Refund needs action',
  cancel_unconfirmed: 'Cancel unconfirmed',
};

/** One line on what a case means, where the label alone isn't enough. */
export const REASON_HELP: Record<string, string> = {
  cancel_unconfirmed:
    "The customer never approved the payment prompt and the provider didn't confirm it was withdrawn after 5 tries. Check the provider's record: was the money taken?",
};

export const reasonHelp = (reason: string): string | undefined => REASON_HELP[reason];

export const reasonLabel = (reason: string): string =>
  REASON_LABEL[reason] || reason.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

export const OUTCOME_LABEL: Record<ResolveOutcome, string> = {
  delivered: 'Delivered — the provider shows it was delivered',
  not_delivered: 'Not delivered — the provider shows it was not delivered',
  collected: 'Collected — the provider shows the money was taken',
  not_collected: 'Not collected — the provider shows no money was taken',
  refunded: 'Refunded — the provider shows the money was returned',
};

/** Outcomes the backend accepts for a case reason. Empty = not resolvable here. */
export function allowedOutcomes(reason: ReconReason): ResolveOutcome[] {
  switch (reason) {
    case 'provider_outcome_unknown':
      return ['delivered', 'not_delivered'];
    case 'payment_outcome_unknown':
    case 'cancel_unconfirmed': // resolved like an unknown payment (commerce resolve.go)
      return ['collected', 'not_collected'];
    case 'refund_unresolved':
      return ['refunded'];
    default:
      return [];
  }
}

/** Token (+ optional units) is required for an electricity delivery. */
export const needsToken = (productType: string | undefined, outcome: ResolveOutcome | undefined): boolean =>
  productType === 'ELECTRICITY' && outcome === 'delivered';

export const EVIDENCE_MIN = 3;
export const EVIDENCE_MAX = 200;
export const NOTE_MAX = 1000;
export const SWITCH_REASON_MAX = 500;

// Minor-unit exponents as commerce-service stores them. TZS is stored in whole
// shillings (product limits are e.g. 500..500000), unlike ISO-4217's 2.
const EXPONENT: Record<string, number> = { TZS: 0, UGX: 0, RWF: 0, KES: 2, USD: 2 };

export function formatMinor(amountMinor: number, currency: string): string {
  const exp = EXPONENT[currency] ?? 2;
  const value = amountMinor / 10 ** exp;
  const n = new Intl.NumberFormat('en-US', { minimumFractionDigits: exp, maximumFractionDigits: exp }).format(value);
  return `${currency} ${n}`;
}

export const shortId = (id: string | undefined | null): string => (id ? id.slice(0, 8) : '—');

// Backend errors come as {code, message} (commerce-service) — show the message verbatim.
export const apiErrorMessage = (e: any, fallback: string): string =>
  e?.response?.data?.message ||
  e?.response?.data?.error?.message ||
  (typeof e?.response?.data?.error === 'string' ? e.response.data.error : undefined) ||
  e?.message ||
  fallback;

// ---------------------------------------------------------------------------
// Provider products — which verified provider code (e.g. Selcom utility code
// "TOP" for prepaid airtime, or a bundle SKU) fulfils a product on a network.
// A provider that needs codes sells NOTHING on a network without an enabled
// mapping, and enabling one needs the provider's own confirmation.
// ---------------------------------------------------------------------------

export interface ProviderProduct {
  id: string;
  provider_code: string;
  product_id: string;
  product_type: string;
  country_code: string;
  operator_code: string;
  provider_product_code: string;
  display_name: string;
  price_minor?: number | null;
  validity: string;
  volume: string;
  mapping_version: number;
  enabled: boolean;
  disabled_reason: string;
  verified_ref: string;
  updated_at: string;
}

export interface ProviderInfo {
  code: string;
  product_types: string[];
  callbacks: boolean;
  disabled_reason?: string;
}

export interface ProviderProductInput {
  /** Set when editing: that mapping's provider/product/network/code can't change. */
  id?: string;
  provider_code: string;
  country_code: string;
  product_type: string;
  operator_code: string;
  provider_product_code: string;
  display_name: string;
  price_minor?: number | null;
  validity: string;
  volume: string;
  enabled: boolean;
  disabled_reason: string;
  verified_ref: string;
}

export const MAPPABLE_PRODUCTS = ['AIRTIME', 'DATA_BUNDLE'];

/** The same validation commerce-service applies, so the form says it first. */
export function providerProductError(v: Partial<ProviderProductInput>, provider?: ProviderInfo): string | null {
  const code = (v.provider_product_code || '').trim();
  if (!v.provider_code) return 'Choose the provider.';
  if (!v.product_type) return 'Choose the product.';
  if (!v.operator_code) return 'Choose the network.';
  if (!code) return 'Enter the provider code (for Selcom airtime: TOP).';
  if (code.length > 40) return 'The provider code can be at most 40 characters.';
  if (v.price_minor != null && !(v.price_minor > 0)) return 'A fixed price must be positive — leave it blank for an open amount.';
  if (v.product_type === 'DATA_BUNDLE' && v.price_minor == null) return 'A data bundle needs its fixed price.';
  if (v.enabled) {
    if ((v.verified_ref || '').trim().length < 3) return "Cite the provider's confirmation of this code before enabling it.";
    if (provider && !provider.product_types.includes(v.product_type) && !provider.product_types.includes('*')) {
      return `${provider.code} can't sell ${productLabel(v.product_type)} right now${provider.disabled_reason ? ` — ${provider.disabled_reason}` : ''}.`;
    }
  }
  return null;
}

/** One mapping per selected network ("All networks" applies the same code to each). */
export function expandNetworks(input: ProviderProductInput, networks: string[]): ProviderProductInput[] {
  if (input.id) return [{ ...input, operator_code: input.operator_code }]; // an edit is exactly one mapping
  return networks.map((n) => ({ ...input, operator_code: n }));
}
